import React, { useEffect, useState } from 'react'
import { DatePicker, Select, Modal } from 'antd'
import { IconButton } from '@material-ui/core'
import { FiChevronRight, FiChevronLeft, FiEyeOff, FiRotateCcw, FiUserX, FiUserCheck } from 'react-icons/fi'
import Rating from '@mui/material/Rating'
import { toast } from 'react-hot-toast'
import moment from 'moment'
import SettingsMenu from '../staticscreens/SettingsMenu'
import { ButtonOutlinedAutoWidth, ButtonFilledAutoWidth } from '../../components/button'
import {
  AdminTeacherFeedbackListService,
  AdminSubmissionStatusService,
  PublicTeacherListService,
  ExcludeTeacherFeedbackService,
  RestoreTeacherFeedbackService,
  ExcludeStudentFeedbackService,
  RestoreStudentFeedbackService
} from '../../services/TeacherFeedbackServices'

export default function AdminTeacherFeedbackList() {
  const [tab, setTab] = useState('Feedback') // 'Feedback' | 'Submission Status'
  const [teachers, setTeachers] = useState([])

  const [month, setMonth] = useState('')
  const [monthValue, setMonthValue] = useState(null) // controlled value for both DatePickers below — keeps them in sync with `month` and with each other/Reset
  const [teacherFilter, setTeacherFilter] = useState('')
  const [text, setText] = useState('')
  const [page, setPage] = useState(1)

  const [feedbackList, setFeedbackList] = useState({})

  const [statusTeacher, setStatusTeacher] = useState('')
  const [statusData, setStatusData] = useState(null)
  const [statusSubTab, setStatusSubTab] = useState('submitted')
  const [statusPage, setStatusPage] = useState(1)

  // Soft-exclude from the report/leaderboard aggregates (not a delete — the
  // row(s) stay, admin can restore later). Two scopes: 'entry' excludes just
  // this one teacher's review, 'student' excludes every review this parent
  // has submitted (scoped to the active month filter, or everything if none
  // is set) — one click instead of excluding each teacher one at a time.
  const [excludeTarget, setExcludeTarget] = useState(null) // { scope: 'entry' | 'student', doc }
  const [excludeReason, setExcludeReason] = useState('')
  const [excludeSaving, setExcludeSaving] = useState(false)

  useEffect(() => {
    PublicTeacherListService().then(res => {
      if (res?.data?.status) setTeachers(res.data.data || [])
    })
  }, [])

  useEffect(() => {
    if (tab === 'Feedback') fetchFeedback()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, page])

  useEffect(() => {
    setStatusPage(1)
    if (tab === 'Submission Status' && statusTeacher) fetchStatus(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, statusTeacher, month])

  useEffect(() => {
    if (tab === 'Submission Status' && statusTeacher) fetchStatus(statusPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusPage])

  async function fetchFeedback(overridePage = page) {
    const res = await AdminTeacherFeedbackListService(overridePage, month, teacherFilter, text)
    if (res?.data?.status) setFeedbackList(res.data)
  }

  async function applyFilter() {
    setPage(1)
    await fetchFeedback(1)
  }

  function handleMonthChange(v, v1) {
    setMonth(v1)
    setMonthValue(v)
  }

  async function resetFilter() {
    setMonth('')
    setMonthValue(null)
    setTeacherFilter('')
    setText('')
    setPage(1)
    const res = await AdminTeacherFeedbackListService(1, '', '', '')
    if (res?.data?.status) setFeedbackList(res.data)
  }

  async function fetchStatus(overridePage = statusPage) {
    setStatusData(null)
    const res = await AdminSubmissionStatusService(statusTeacher, month, overridePage)
    if (res?.data?.status) setStatusData(res.data)
  }

  async function confirmExclude() {
    if (!excludeTarget) return
    setExcludeSaving(true)
    const res = excludeTarget.scope === 'student'
      ? await ExcludeStudentFeedbackService(excludeTarget.doc.student?._id, month, excludeReason)
      : await ExcludeTeacherFeedbackService(excludeTarget.doc._id, excludeReason)
    setExcludeSaving(false)
    if (res?.data?.status) {
      toast.success(res.data.message || 'Excluded from reports')
      setExcludeTarget(null)
      setExcludeReason('')
      fetchFeedback()
    } else {
      toast.error(res?.data?.message || 'Could not exclude')
    }
  }

  async function handleRestore(id) {
    const res = await RestoreTeacherFeedbackService(id)
    if (res?.data?.status) {
      toast.success('Restored — this entry counts again')
      fetchFeedback()
    } else {
      toast.error(res?.data?.message || 'Could not restore this entry')
    }
  }

  async function handleRestoreStudent(studentId) {
    const res = await RestoreStudentFeedbackService(studentId, month)
    if (res?.data?.status) {
      toast.success(res.data.message || 'Restored')
      fetchFeedback()
    } else {
      toast.error(res?.data?.message || 'Could not restore')
    }
  }

  const teacherOptions = teachers.map(t => ({ label: t.name, value: t._id }))
  const datas = feedbackList?.datas || []
  const pagination = feedbackList?.pagination || {}

  const avgRating = datas.length ? (datas.reduce((s, d) => s + (d.average_rating || 0), 0) / datas.length).toFixed(2) : '0.00'
  const ratedTeacherCount = new Set(datas.map(d => d.teacher?._id)).size

  return (
    <div className='md:h-screen pt-5 md:pt-0 md:max-h-screen box-border md:overflow-hidden'>

      <Modal
        open={!!excludeTarget}
        closable={false}
        footer={false}
        width={340}>
        <div className='relative'>
          <h6 className='font-bold text-[15px] mb-2 w-full'>{excludeTarget?.scope === 'student' ? 'Exclude This Parent' : 'Exclude from Reports'}</h6>
          <h6 className='bg-slate-100 text-[12px] p-1.5 font-[400]'>
            {excludeTarget?.scope === 'student' ? (
              <>This removes <span className='font-[700]'>every review</span> <span className='font-[700]'>{excludeTarget?.doc?.student?.name}</span> has submitted{month ? <> for <span className='font-[700]'>{moment(month, 'YYYY-MM').format('MMMM YYYY')}</span></> : ' across every month'} from the leaderboard, rank, and average rating — nothing is deleted, and you can restore it any time.</>
            ) : (
              <>This removes <span className='font-[700]'>{excludeTarget?.doc?.student?.name}</span>'s review of <span className='font-[700]'>{excludeTarget?.doc?.teacher?.name}</span> from the leaderboard, rank, and average rating — it's not deleted, and you can restore it any time.</>
            )}
          </h6>
          <textarea
            value={excludeReason}
            onChange={(e) => setExcludeReason(e.target.value)}
            placeholder='Reason (optional) — shown to other admins'
            rows={2}
            className='w-full border border-slate-300 rounded-md px-2 py-1.5 text-[12px] mt-2 focus:outline-none'
          />
          <div className='flex justify-end mt-3'>
            <ButtonOutlinedAutoWidth btnName='Cancel' onClick={() => { setExcludeTarget(null); setExcludeReason('') }} />
            <div className='ml-2'>
              <ButtonFilledAutoWidth btnName={excludeSaving ? 'Excluding…' : 'Exclude'} onClick={confirmExclude} />
            </div>
          </div>
        </div>
      </Modal>

      <div className='md:flex'>
        <div>
          <SettingsMenu />
        </div>
        <div className='md:w-[90%] pl-5 mt-4'>

          <div className='md:flex items-center -mt-1 border-b justify-between pb-1'>
            <h6 className='font-[800] text-[13px]'>Parent Feedback & Teacher Ratings</h6>
          </div>

          {/* Stat cards — "more attractive and informative" */}
          <div className='grid grid-cols-2 md:grid-cols-4 gap-2 my-3'>
            <StatCard label='Total Feedback' value={pagination?.total ?? 0} color='#158a93' />
            <StatCard label='Avg Rating (this page)' value={`${avgRating} / 5`} color='#de9f19' />
            <StatCard label='Teachers Rated (this page)' value={ratedTeacherCount} color='#6b7fd7' />
            <StatCard label='Current Month' value={moment().format('MMM YYYY')} color='#2a297e' />
          </div>

          <div className='flex items-center border-y overflow-x-auto flex-nowrap'>
            {['Feedback', 'Submission Status'].map((s) => (
              <h6 key={s} onClick={() => setTab(s)} className={`font-[600] ${tab === s && 'bg-[#2a297e] text-white'} flex-shrink-0 text-center cursor-pointer py-1 w-auto px-3 text-[11px]`}>{s}</h6>
            ))}
          </div>

          {tab === 'Feedback' ? (
            <>
              <div className='flex flex-wrap items-center text-[12px] mt-3'>
                <DatePicker
                  size='small'
                  picker='month'
                  format='YYYY-MM'
                  placeholder='Filter by Month'
                  style={{ fontSize: '11px' }}
                  className='text-[11px] py-[2.6px] w-32 border-slate-300 mr-2'
                  value={monthValue}
                  onChange={handleMonthChange}
                />
                <Select
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  value={teacherFilter || null}
                  bordered={false}
                  allowClear
                  onClear={() => setTeacherFilter('')}
                  className='border w-[180px] mr-2 rounded-md h-[28px] border-slate-300'
                  options={teacherOptions}
                  placeholder={<span className='text-[12px] text-gray-300'>Filter by Teacher</span>}
                  onChange={(e) => setTeacherFilter(e || '')}
                />
                <input
                  type='text'
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') applyFilter() }}
                  placeholder='Search student / mobile'
                  className='border border-slate-300 rounded-md h-[28px] px-2 text-[11px] mr-2 w-[180px]'
                />
                <div className='mr-2 mt-1 md:mt-0'>
                  <ButtonOutlinedAutoWidth btnName='Apply Filter' onClick={applyFilter} />
                </div>
                <div className='mr-2 mt-1 md:mt-0'>
                  <ButtonOutlinedAutoWidth btnName='Reset Filter' onClick={resetFilter} />
                </div>

                <div className='flex items-center ml-auto'>
                  <h6 className='mr-2 font-[600]'>{page === 1 ? (datas.length > 0 ? 1 : 0) : (page - 1) * (pagination?.limit || 25)} - {pagination?.limit || 25} of {pagination?.total || 0}</h6>
                  <IconButton onClick={() => { if (page > 1) setPage(page - 1) }}><FiChevronLeft className={`${page === 1 ? 'opacity-50' : 'opacity-100'}`} size={16} /></IconButton>
                  <IconButton onClick={() => { if (page < pagination?.totalPages) setPage(page + 1) }}><FiChevronRight className={`${(pagination?.totalPages === page || datas.length === 0) ? 'opacity-50' : 'opacity-100'}`} size={16} /></IconButton>
                </div>
              </div>

              {datas.length === 0 && (
                <div className='grid place-items-center mt-20 items-center justify-center'>
                  <img alt='No data' src={'https://fidecrmfiles.s3.amazonaws.com/NoDataFound1.png'} className='w-40 h-40 object-contain' />
                  <h6 className='font-bold text-[14px] -mt-10'>No feedback found</h6>
                  <h6 className='font-[500] w-[70%] text-center text-slate-700 text-[12.5px] -mt-2'>Try adjusting the month, teacher, or search filters.</h6>
                </div>
              )}

              {datas.length > 0 && (
                <div className='md:max-h-[75vh] mt-3 md:overflow-auto border-t border-l border-r'>
                  <div className='sticky top-0 z-50 flex w-max md:w-[100%] bg-white border-b border-slate-200 relative'>
                    <h6 className='sticky top-0 z-50 text-[12px] min-w-[50px] w-[50px] px-2 py-1 font-[600] text-slate-800 border-r flex items-center justify-center'>Sl No</h6>
                    <h6 className='sticky top-0 z-50 text-[12px] min-w-[180px] w-[180px] px-2 py-1 font-[600] text-slate-800 border-r'>Student</h6>
                    <h6 className='sticky top-0 z-50 text-[12px] min-w-[160px] w-[160px] px-2 py-1 font-[600] text-slate-800 border-r'>Teacher</h6>
                    <h6 className='sticky top-0 z-50 text-[12px] min-w-[80px] w-[80px] px-2 py-1 font-[600] text-slate-800 border-r'>Month</h6>
                    <h6 className='sticky top-0 z-50 text-[12px] min-w-[150px] w-[150px] px-2 py-1 font-[600] text-slate-800 border-r'>Rating</h6>
                    <h6 className='sticky top-0 z-50 text-[12px] min-w-[300px] w-[300px] px-2 py-1 font-[600] text-slate-800 border-r'>Comments</h6>
                    <h6 className='sticky top-0 z-50 text-[12px] min-w-[150px] w-[150px] px-2 py-1 font-[600] text-slate-800 border-r'>Submitted</h6>
                    <h6 className='sticky top-0 z-50 text-[12px] min-w-[150px] w-[150px] px-2 py-1 font-[600] text-slate-800'>Report</h6>
                  </div>
                  {datas.map((d, i) => (
                    <div key={d._id} className={`flex w-max md:w-[100%] text-[11px] border-b border-slate-200 relative ${d.excluded_from_report ? 'bg-slate-50' : 'bg-white'}`}>
                      <h6 className={`text-[12px] min-w-[50px] w-[50px] px-2 py-1 font-[500] border-r flex items-start justify-center ${d.excluded_from_report ? 'bg-slate-50' : 'bg-white'}`}>{page > 1 ? i + 1 + ((pagination?.limit || 25) * (page - 1)) : i + 1}</h6>
                      <h6 className='text-[12px] min-w-[180px] w-[180px] px-2 py-1 font-[500] border-r'>
                        {d.student?.name}
                        <div className='text-[10px] text-slate-400'>{d.student?.mobile}</div>
                      </h6>
                      <h6 className='text-[12px] min-w-[160px] w-[160px] px-2 py-1 font-[500] border-r'>{d.teacher?.name}</h6>
                      <h6 className='text-[12px] min-w-[80px] w-[80px] px-2 py-1 font-[500] border-r'>{d.month}</h6>
                      <h6 className='text-[12px] min-w-[150px] w-[150px] px-2 py-1 border-r'>
                        <Rating value={d.average_rating} precision={0.1} readOnly size='small' />
                        <div className='text-[10px] text-slate-500'>{d.average_rating} / 5</div>
                      </h6>
                      <h6 className='text-[11px] min-w-[300px] w-[300px] px-2 py-1 border-r'>
                        {d.feedback_text}
                        {d.voice_feedback_url && <audio controls src={d.voice_feedback_url} className='w-full h-[30px] mt-1 block' />}
                        {d.updated_by_admin && <div className='text-[10px] text-[#de9f19] font-[600] mt-0.5'>Edited by admin</div>}
                      </h6>
                      <h6 className='text-[10px] min-w-[150px] w-[150px] px-2 py-1 text-slate-500 border-r'>{moment(d.createdAt).format('LLL')}</h6>
                      <h6 className='text-[11px] min-w-[150px] w-[150px] px-2 py-1'>
                        {d.excluded_from_report ? (
                          <>
                            <div className='text-[9px] font-[700] text-red-500 uppercase'>Excluded</div>
                            {d.excluded_reason && <div className='text-[10px] text-slate-500 italic'>"{d.excluded_reason}"</div>}
                            {d.excluded_by?.name && <div className='text-[9px] text-slate-400'>by {d.excluded_by.name}</div>}
                            <button onClick={() => handleRestore(d._id)} className='flex items-center gap-1 text-[10px] font-[600] text-[#2a297e] mt-1'>
                              <FiRotateCcw size={11} /> Restore
                            </button>
                            <button onClick={() => handleRestoreStudent(d.student?._id)} className='flex items-center gap-1 text-[10px] font-[600] text-[#2a297e] mt-1'>
                              <FiUserCheck size={11} /> Restore Parent
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => { setExcludeTarget({ scope: 'entry', doc: d }); setExcludeReason('') }} className='flex items-center gap-1 text-[10px] font-[600] text-slate-500 hover:text-red-500'>
                              <FiEyeOff size={11} /> Exclude
                            </button>
                            <button onClick={() => { setExcludeTarget({ scope: 'student', doc: d }); setExcludeReason('') }} className='flex items-center gap-1 text-[10px] font-[600] text-slate-500 hover:text-red-500 mt-1'>
                              <FiUserX size={11} /> Exclude Parent
                            </button>
                          </>
                        )}
                      </h6>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className='mt-3'>
              <div className='flex flex-wrap items-center text-[12px]'>
                <DatePicker
                  size='small'
                  picker='month'
                  format='YYYY-MM'
                  placeholder='Month'
                  style={{ fontSize: '11px' }}
                  className='text-[11px] py-[2.6px] w-32 border-slate-300 mr-2'
                  value={monthValue}
                  onChange={handleMonthChange}
                />
                <Select
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  value={statusTeacher || null}
                  bordered={false}
                  className='border w-[220px] mr-2 rounded-md h-[28px] border-slate-300'
                  options={teacherOptions}
                  placeholder={<span className='text-[12px] text-gray-300'>Select a teacher…</span>}
                  onChange={(e) => setStatusTeacher(e || '')}
                />
              </div>

              {!statusTeacher ? (
                <div className='grid place-items-center mt-20'>
                  <h6 className='font-[500] text-slate-500 text-[12.5px]'>Select a teacher to see who has and hasn't submitted feedback this month.</h6>
                </div>
              ) : !statusData ? (
                <div className='grid place-items-center mt-20'>
                  <h6 className='font-[500] text-slate-400 text-[12.5px]'>Loading…</h6>
                </div>
              ) : (
                <>
                  <div className='md:flex items-center justify-between border-y mt-3'>
                    <div className='flex items-center overflow-x-auto flex-nowrap'>
                      {[
                        { key: 'submitted', label: `Submitted (${statusData.submitted.total})` },
                        { key: 'not_submitted', label: `Not Submitted (${statusData.not_submitted.total})` }
                      ].map(s => (
                        <h6 key={s.key} onClick={() => setStatusSubTab(s.key)} className={`font-[600] ${statusSubTab === s.key && 'bg-[#2a297e] text-white'} flex-shrink-0 text-center cursor-pointer py-1 w-auto px-3 text-[11px]`}>{s.label}</h6>
                      ))}
                    </div>
                    {(() => {
                      const activeTotal = statusSubTab === 'submitted' ? statusData.submitted.total : statusData.not_submitted.total
                      const activeLimit = statusData?.pagination?.limit || 25
                      const activeTotalPages = Math.ceil(activeTotal / activeLimit) || 1
                      return (
                        <div className='flex items-center text-[12px] py-1'>
                          <h6 className='mr-2 font-[600]'>{statusPage === 1 ? (activeTotal > 0 ? 1 : 0) : (statusPage - 1) * activeLimit} - {activeLimit} of {activeTotal}</h6>
                          <IconButton onClick={() => { if (statusPage > 1) setStatusPage(statusPage - 1) }}><FiChevronLeft className={`${statusPage === 1 ? 'opacity-50' : 'opacity-100'}`} size={16} /></IconButton>
                          <IconButton onClick={() => { if (statusPage < activeTotalPages) setStatusPage(statusPage + 1) }}><FiChevronRight className={`${statusPage >= activeTotalPages ? 'opacity-50' : 'opacity-100'}`} size={16} /></IconButton>
                        </div>
                      )
                    })()}
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-5 gap-2 mt-3'>
                    {(statusSubTab === 'submitted' ? statusData.submitted.data : statusData.not_submitted.data).map(s => (
                      <div key={s._id} className='border border-slate-200 rounded-md px-3 py-2 flex flex-wrap items-center justify-between'>
                        <span className='text-[11px] font-[500] text-slate-800'>{s.name}</span>
                        <span className='text-[10px] text-slate-400'>Ph No : {s.mobile}</span>
                      </div>
                    ))}
                  </div>
                  {(statusSubTab === 'submitted' ? statusData.submitted.data : statusData.not_submitted.data).length === 0 && (
                    <h6 className='text-center text-slate-400 text-[12.5px] mt-10'>Nothing to show here.</h6>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function StatCard({ label, value, color }) {
  return (
    <div className='border border-slate-200 px-3 py-2' style={{ borderLeft: `3px solid ${color}` }}>
      <h6 className='text-[9px] font-[600] text-slate-400 uppercase'>{label}</h6>
      <h6 className='text-[14px] font-[800] text-slate-800 mt-0.5'>{value}</h6>
    </div>
  )
}
